import React from 'react';

import { useSendInvitesToEmployersByPeoStaffMutation } from 'src/api';
import { useNotification } from 'src/hooks/app';
import { TableDataProvider } from 'src/providers/TableDataProvider';
import { clearEntitiesData } from 'src/utils/bulkUpload';

import { EmployersUploadForm, EmployersUploadTable } from './components';
import { StepId } from './constants';
import { ExtendedInvitedEmployerData, InvitedEmployerData } from './types';
import { validateEmployers } from './utils/validateEmployers';

interface InviteEmployersAdminsByPeoStaffModalContentProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InviteEmployersAdminsByPeoStaffModalContent: React.FC<
  InviteEmployersAdminsByPeoStaffModalContentProps
> = ({ closeModal, switchClosability }) => {
  const [currentStep, setCurrentStep] = React.useState<StepId>(StepId.Upload);

  const [employers, setEmployers] = React.useState<ExtendedInvitedEmployerData[]>([]);

  const { createApolloContext } = useNotification();

  const [sendInvites, { loading: sendInvitesLoading }] =
    useSendInvitesToEmployersByPeoStaffMutation({
      context: createApolloContext({
        errorMessage: 'Failed to invite employers',
        successMessage: 'Employers have been successfully invited',
      }),
    });

  const handleEmployersData = React.useCallback((employers: InvitedEmployerData[]) => {
    const validatedEmployers = validateEmployers(employers);
    setEmployers(validatedEmployers);
    setCurrentStep(StepId.Review);
  }, []);

  const handleSaveChanges = React.useCallback(async () => {
    try {
      switchClosability(false);

      const validEmployers = employers.filter(employer => employer.valid);

      if (validEmployers.length > 0) {
        await sendInvites({
          variables: {
            input: { employers: clearEntitiesData(validEmployers) },
          },
        });
      }

      switchClosability(true);
      closeModal();
    } catch {
      switchClosability(true);
    }
  }, [closeModal, employers, sendInvites, switchClosability]);

  const handleCancel = React.useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleBack = React.useCallback(() => {
    setEmployers([]);
    setCurrentStep(StepId.Upload);
  }, []);

  const loading = sendInvitesLoading;

  if (currentStep === StepId.Upload) {
    return (
      <EmployersUploadForm
        handleEmployersData={handleEmployersData}
        handleCancel={handleCancel}
        switchClosability={switchClosability}
        nextStepLoading={loading}
        uploadFormTopNotice={uploadFormTopNotice}
      />
    );
  }

  return (
    <TableDataProvider>
      <EmployersUploadTable
        employers={employers}
        handleSaveChanges={handleSaveChanges}
        handleBack={handleBack}
        loading={loading}
      />
    </TableDataProvider>
  );
};

const uploadFormTopNotice = `Using this modal, you can upload a file with Employers under your PEO company that should be added to the platform. Please note that the Employers you add must be reviewed by the Clear Verify Team.`;
